import { FCW } from 'types';
import { KeyboardEvent, useCallback, useRef } from 'react';

export const AccordionItemContent: FCW = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const isTextLikeInput = (el: HTMLElement) => {
    if (el.tagName.toLowerCase() === 'textarea') return true;
    if (el.tagName.toLowerCase() !== 'input') return false;
    const type = (el as HTMLInputElement).type;
    return ['text', 'search', 'url', 'email', 'password', 'number', 'tel'].includes(type);
  };

  const isVisible = (el: HTMLElement) => el.offsetParent !== null || el.getClientRects().length > 0;

  const getFocusable = (root: HTMLElement) =>
    Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]):not([aria-disabled="true"]), input:not([type="hidden"]):not([disabled]):not([aria-disabled="true"]), select:not([disabled]):not([aria-disabled="true"]), textarea:not([disabled]):not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => isVisible(el));

  const onContainerKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

    const target = e.target as HTMLElement;
    // Don’t hijack arrow keys for editing unless Ctrl/Meta is held
    if (!e.ctrlKey && !e.metaKey) {
      if (target.tagName.toLowerCase() === 'select' || isTextLikeInput(target)) {
        return;
      }
    }

    const container = containerRef.current;
    if (!container) return;

    const focusables = getFocusable(container);
    if (focusables.length === 0) return;

    const active = (document.activeElement as HTMLElement) || target;
    const currentIndex = focusables.findIndex((el) => el === active || el.contains(active));
    const backwards = e.key === 'ArrowUp';

    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = backwards ? focusables.length - 1 : 0;
    } else if (backwards) {
      nextIndex = currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1;
    }

    e.preventDefault();
    focusables[nextIndex]?.focus();
  }, []);

  return (
    <div className="accordion-item-content" role="group" ref={containerRef} onKeyDown={onContainerKeyDown}>
      {children}
    </div>
  );
};
